import { Alert, Box, Button, Stack, TextField, Typography, useTheme } from '@mui/material';
import { useRef, useState } from 'react';
import type { FormEvent, KeyboardEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';

import { normaliseError } from '@/api/baseQuery';
import { useRequestOtpMutation, useVerifyOtpMutation } from '@/api/generated/authApi';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { Turnstile, captchaEnabled } from '@/components/common/Turnstile';
import { selectAuthStatus, sessionEstablished } from '@/features/auth/authSlice';
import { DEFAULT_ROUTE } from '@/routes/routeConfig';

// ── Validators ───────────────────────────────────────────────────────────────
//
// The backend normalises phone to E.164 and emits uniform failure messages, but a client-side
// gate stops obvious nonsense from spending an SMS or a login attempt.

/** True once the user has typed something that could plausibly be an Indian or E.164 number. */
function isPlausiblePhone(raw: string): boolean {
  const digits = raw.replace(/\D/g, '');
  return digits.length >= 10 && digits.length <= 15;
}

// ── Brand side ────────────────────────────────────────────────────────────────

function BrandSide() {
  return (
    <Box
      sx={{
        flex: 1,
        background: 'linear-gradient(150deg, #0E1524 0%, #1B2A5C 55%, #0FB5A6 160%)',
        color: '#fff',
        p: { md: '56px 48px' },
        display: { xs: 'none', md: 'flex' },
        flexDirection: 'column',
        justifyContent: 'space-between',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <svg
        viewBox="0 0 500 700"
        preserveAspectRatio="xMidYMid slice"
        fill="none"
        style={{ position: 'absolute', inset: 0, opacity: 0.5, pointerEvents: 'none' }}
        aria-hidden="true"
      >
        <g stroke="rgba(255,255,255,.16)" strokeWidth={1.5}>
          <path d="M-40 140 C 140 60, 320 220, 560 120" />
          <path d="M-40 220 C 160 150, 300 300, 560 210" />
          <path d="M-40 320 C 120 260, 340 400, 560 300" />
          <path d="M-40 430 C 180 360, 300 520, 560 420" />
          <path d="M-40 540 C 140 470, 340 620, 560 520" />
        </g>
        <g fill="rgba(15,181,166,.9)">
          <circle cx={150} cy={120} r={3.5} />
          <circle cx={320} cy={300} r={3.5} />
          <circle cx={240} cy={470} r={3.5} />
        </g>
      </svg>

      <Box sx={{ position: 'relative', zIndex: 2, maxWidth: 440 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '11px', mb: '34px' }}>
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: '9px',
              background: 'linear-gradient(140deg,#0FB5A6,#2A47C9)',
              display: 'grid',
              placeItems: 'center',
              boxShadow: '0 4px 14px -4px rgba(15,181,166,.6)',
            }}
          >
            <svg viewBox="0 0 24 24" fill="none" width={20} height={20}>
              <path
                d="M3 8c4 0 4 3 9 3s5-3 9-3M3 15c4 0 4 3 9 3s5-3 9-3"
                stroke="#fff"
                strokeWidth={2}
                strokeLinecap="round"
              />
            </svg>
          </Box>
          <Typography
            sx={{
              fontFamily: '"Sora", system-ui, sans-serif',
              fontWeight: 700,
              fontSize: '1.25rem',
              color: '#fff',
            }}
          >
            OTrack
          </Typography>
        </Box>

        <Typography
          component="h1"
          sx={{
            fontFamily: '"Sora", system-ui, sans-serif',
            fontSize: { md: '2.375rem', lg: '2.75rem' },
            fontWeight: 700,
            lineHeight: 1.1,
            mb: 2,
            textWrap: 'balance',
          }}
        >
          Every order, one conversation.
        </Typography>
        <Typography sx={{ color: '#C7D2F0', fontSize: '0.9375rem' }}>
          Link each order to your customer and talk it through in one chat: updates, bills and
          photos, with the link sent to them on Slack.
        </Typography>

        <Box sx={{ display: 'flex', gap: '10px', flexWrap: 'wrap', mt: '28px' }}>
          {['Chat per order', 'POS bills and photos', 'Status updates', 'Slack links'].map((b) => (
            <Box
              key={b}
              component="span"
              sx={{
                fontSize: 12,
                bgcolor: 'rgba(255,255,255,.1)',
                border: '1px solid rgba(255,255,255,.18)',
                px: '11px',
                py: '6px',
                borderRadius: '99px',
                color: '#DCE4F7',
              }}
            >
              {b}
            </Box>
          ))}
        </Box>
      </Box>

      <Typography sx={{ position: 'relative', zIndex: 2, color: '#8FA0C9', fontSize: 12 }}>
        v0.9.0-preview · Secure sign-in
      </Typography>
    </Box>
  );
}

// ── OTP input row ─────────────────────────────────────────────────────────────

function OtpInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const refs = useRef<Array<HTMLInputElement | null>>([]);
  const digits = value.padEnd(6, ' ').split('').slice(0, 6);

  const update = (i: number, v: string) => {
    // Reject anything that isn't a digit — spares the user an "OTP is digits only" error.
    const digit = v.replace(/\D/g, '').slice(-1);
    const arr = [...digits.map((d) => (d === ' ' ? '' : d))];
    arr[i] = digit;
    const next = arr.join('').replace(/ /g, '');
    onChange(next.padEnd(6, ' ').slice(0, 6).replace(/ /g, ''));
    if (digit && i < 5) refs.current[i + 1]?.focus();
  };

  const handleKey = (i: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !(digits[i] ?? '').trim() && i > 0) {
      refs.current[i - 1]?.focus();
    }
  };

  // Pasting a full 6-digit code into the first box fills every box, which is what every other
  // OTP form in the user's banking apps does, so meeting that expectation is cheap.
  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const text = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (text.length > 1) {
      e.preventDefault();
      onChange(text);
      refs.current[Math.min(text.length, 5)]?.focus();
    }
  };

  return (
    <Box sx={{ display: 'flex', gap: { xs: '6px', sm: '8px' }, width: '100%' }}>
      {digits.map((d, i) => (
        <Box
          key={i}
          component="input"
          ref={(el: HTMLInputElement | null) => {
            refs.current[i] = el;
          }}
          maxLength={1}
          value={d.trim()}
          onChange={(e) => update(i, e.target.value)}
          onKeyDown={(e) => handleKey(i, e)}
          onPaste={handlePaste}
          inputMode="numeric"
          autoComplete="one-time-code"
          sx={{
            // flex-basis: 0 with minWidth: 0 lets six boxes share the row evenly
            // without the intrinsic input width pushing them off-screen. No
            // explicit width — flex owns the sizing.
            flex: '1 1 0',
            minWidth: 0,
            height: { xs: 44, sm: 52 },
            textAlign: 'center',
            fontFamily: '"IBM Plex Mono", monospace',
            fontSize: { xs: '1.0625rem', sm: '1.25rem' },
            fontWeight: 600,
            px: 0,
            py: 0,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            bgcolor: 'background.paper',
            color: 'text.primary',
            outline: 'none',
            '&:focus': {
              borderColor: 'primary.main',
              boxShadow: (t) => `0 0 0 3px ${t.palette.primary.main}22`,
            },
          }}
        />
      ))}
    </Box>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────

export default function LoginPage() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const status = useAppSelector(selectAuthStatus);

  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState<'phone' | 'code'>('phone');
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // ── CAPTCHA (only when VITE_TURNSTILE_SITE_KEY is set). A token is single use, so every
  // failed attempt remounts the widget for a fresh one.
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [captchaReset, setCaptchaReset] = useState(0);
  const captchaMissing = captchaEnabled && !captchaToken;

  const [requestOtp, requestState] = useRequestOtpMutation();
  const [verifyOtp, verifyState] = useVerifyOtpMutation();

  const requestedFrom = (location.state as { from?: { pathname: string } } | null)?.from?.pathname;

  if (status === 'authenticated') {
    return <Navigate to={requestedFrom ?? DEFAULT_ROUTE} replace />;
  }

  function clearErrors() {
    setError(null);
    setFieldErrors({});
  }

  async function handleRequestOtp(event: FormEvent) {
    event.preventDefault();
    clearErrors();

    if (!phone.trim()) {
      setFieldErrors({ phone: 'Enter your mobile number' });
      return;
    }
    if (!isPlausiblePhone(phone)) {
      setFieldErrors({ phone: 'That does not look like a 10-digit mobile number' });
      return;
    }

    try {
      await requestOtp({ requestOtpForm: { phone } }).unwrap();
      setStep('code');
    } catch (err) {
      setError(normaliseError(err).message);
    }
  }

  async function handleVerifyOtp(event: FormEvent) {
    event.preventDefault();
    clearErrors();

    const cleaned = code.replace(/\D/g, '');
    if (cleaned.length !== 6) {
      setFieldErrors({ code: 'Enter the 6-digit code' });
      return;
    }
    if (captchaMissing) {
      setError('Complete the CAPTCHA and try again');
      return;
    }

    try {
      const response = await verifyOtp({
        verifyOtpForm: { phone, code: cleaned, captchaToken: captchaToken ?? undefined },
      }).unwrap();
      if (response.accessToken) {
        dispatch(sessionEstablished(response));
        // A deep link that bounced to sign-in goes back where it was headed.
        navigate(requestedFrom ?? DEFAULT_ROUTE, { replace: true });
      } else {
        setError('The server did not return a session. Please try again.');
      }
    } catch (err) {
      setError(normaliseError(err).message);
      setCaptchaReset((n) => n + 1);
    }
  }

  const busy = requestState.isLoading || verifyState.isLoading;

  return (
    <Box sx={{ position: 'fixed', inset: 0, display: 'flex', bgcolor: 'background.default' }}>
      <BrandSide />

      <Box
        sx={{
          width: { xs: '100%', md: 'min(460px, 100%)' },
          p: { xs: '32px 24px', md: '48px' },
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          gap: '6px',
          overflowY: 'auto',
          bgcolor: 'background.paper',
        }}
      >
        {/* Logo */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', mb: '20px' }}>
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: '9px',
              background: 'linear-gradient(140deg,#0FB5A6,#2A47C9)',
              display: 'grid',
              placeItems: 'center',
              boxShadow: '0 4px 14px -4px rgba(15,181,166,.6)',
            }}
          >
            <svg viewBox="0 0 24 24" fill="none" width={20} height={20}>
              <path
                d="M3 8c4 0 4 3 9 3s5-3 9-3M3 15c4 0 4 3 9 3s5-3 9-3"
                stroke="#fff"
                strokeWidth={2}
                strokeLinecap="round"
              />
            </svg>
          </Box>
          <Typography
            sx={{
              fontFamily: '"Sora", system-ui, sans-serif',
              fontWeight: 700,
              fontSize: '1.0625rem',
            }}
          >
            OTrack
          </Typography>
        </Box>

        <Typography
          component="h2"
          sx={{
            fontFamily: '"Sora", system-ui, sans-serif',
            fontSize: '1.375rem',
            fontWeight: 600,
            mb: '4px',
          }}
        >
          Sign in to your portal
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: '18px' }}>
          Vendors — sign in with your mobile number. A new number gets an account.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {step === 'phone' && (
          <Box component="form" onSubmit={handleRequestOtp} noValidate>
            <Stack spacing={2}>
              <TextField
                label="Mobile number"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                autoComplete="tel"
                autoFocus
                required
                fullWidth
                inputMode="tel"
                placeholder="+91 98765 43210"
                error={Boolean(fieldErrors.phone)}
                helperText={fieldErrors.phone ?? 'Any common format works — we normalise it.'}
                inputProps={{ maxLength: 20 }}
              />
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={busy}
                sx={{ borderRadius: 2, py: 1.5 }}
              >
                {busy ? 'Sending…' : 'Send OTP'}
              </Button>
            </Stack>
          </Box>
        )}

        {step === 'code' && (
          <Box component="form" onSubmit={handleVerifyOtp} noValidate>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              6-digit code sent to <strong>{phone}</strong>. Valid for 5 minutes.
            </Typography>
            <Stack spacing={2}>
              <Box>
                <OtpInput value={code} onChange={setCode} />
                {fieldErrors.code && (
                  <Typography
                    variant="caption"
                    color="error.main"
                    sx={{ mt: 0.5, display: 'block' }}
                  >
                    {fieldErrors.code}
                  </Typography>
                )}
              </Box>
              <Turnstile action="signup" onToken={setCaptchaToken} resetKey={captchaReset} />
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={busy || captchaMissing || code.replace(/\D/g, '').length < 6}
                sx={{ borderRadius: 2, py: 1.5 }}
              >
                {busy ? 'Verifying…' : 'Verify & continue'}
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={() => {
                  setStep('phone');
                  setCode('');
                  clearErrors();
                }}
                sx={{ borderRadius: 2 }}
              >
                ← Change number
              </Button>
            </Stack>
          </Box>
        )}

        {import.meta.env.DEV && (
          <Box
            sx={{
              mt: '14px',
              fontSize: '12.5px',
              color: 'text.secondary',
              bgcolor: isDark ? 'rgba(255,255,255,.03)' : '#FBFCFE',
              border: '1px dashed',
              borderColor: 'divider',
              borderRadius: 2,
              p: '10px 12px',
            }}
          >
            🔒 <strong>Development</strong> — the OTP is logged at INFO on the backend and emailed
            to Mailpit (
            <a href="http://localhost:8025" target="_blank" rel="noreferrer">
              localhost:8025
            </a>
            ).
          </Box>
        )}
      </Box>
    </Box>
  );
}
